import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileOpenParams} from 'file-browser/FileBrowserActions';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as navigation from 'Navigation';
import {asJQueryPromise} from 'util/Promises';

import {
  CanvasFolderArtifactFileStateQuery,
  CanvasFolderArtifactFileStateQueryVariables,
} from './__generated/CanvasFolderArtifactFileStateQuery';
import {
  canvasFolderArtifactFileStateDataToAttributes,
  getCanvasFolderArtifactFileStateQueryOptions,
} from './CanvasFolderArtifactGraphQL';
import CanvasFolderArtifactTabState from './CanvasFolderArtifactTabState';

interface CanvasFolderArtifactFileStateAttributes {
  name?: string;
  editUrl?: string;
  hideTabNav?: boolean;
  archiveRecord?: ArchiveRecord | null;
  permissions?: Permissions | null;
}

class CanvasFolderArtifactFileState extends ApolloFileState<
  CanvasFolderArtifactFileStateQuery,
  CanvasFolderArtifactFileStateQueryVariables,
  CanvasFolderArtifactFileStateAttributes
> {
  static fileType = ModelTypeConstants.CANVAS_FOLDER_ARTIFACT;

  static override getQueryOptions(
    fileId: string
  ): ReturnType<typeof getCanvasFolderArtifactFileStateQueryOptions> {
    return getCanvasFolderArtifactFileStateQueryOptions(fileId);
  }

  static override dataToAttributes(
    data: CanvasFolderArtifactFileStateQuery
  ): CanvasFolderArtifactFileStateAttributes {
    return canvasFolderArtifactFileStateDataToAttributes(data);
  }

  static override openFile(
    fileId: string,
    _queryKey?: FileOpenParams
  ): JQueryPromise<CanvasFolderArtifactFileState> {
    return asJQueryPromise(getApolloClient().query(this.getQueryOptions(fileId))).then(
      (response) =>
        new this({
          id: fileId,
          ...(response != null ? this.dataToAttributes(response.data) : {}),
        })
    );
  }

  canvasTab!: CanvasFolderArtifactTabState;

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override initialize(...args: any): void {
    super.initialize(...args);

    this.canvasTab = new CanvasFolderArtifactTabState({
      canvasFolderArtifactId: this.id,
      title: this.name(),
    });
    this.on('change:name', () => {
      this.canvasTab.set('title', this.name());
    });

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(this.canvasTab);
    buffer.tabs().at(0).set({active: true});
  }

  override editURL(): string {
    return this.get('editUrl') ?? '';
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set('archiveRecord', archiveRecord);
  }

  override permissions(): Permissions | null {
    return this.get('permissions') ?? null;
  }

  override setPermissions(permissions: Permissions): void {
    this.set('permissions', permissions);
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name change:editUrl', () => {
      this.syncTitleAndURL();
    });
  }
}

export default CanvasFolderArtifactFileState;
